import { useEffect, useRef } from 'react'
import PrivacyPage from '../../../pages/PrivacyPage'
import TermsPage from '../../../pages/TermsPage'

export type LegalDoc = 'privacy' | 'terms'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

/**
 * Renders the Privacy Policy or Terms of Service in an overlay so onboarding
 * users can read them without navigating away and losing their place (B-14).
 * The legal pages render in `embedded` mode (body only); the modal supplies the
 * scroll container and dismiss affordance.
 *
 * Accessibility: focus is trapped within the dialog while open (Tab/Shift+Tab
 * cycle inside it instead of leaking to the obscured page behind), focus moves
 * into the dialog on open, and returns to the trigger element on close.
 */
export default function LegalModal({ doc, onClose }: { doc: LegalDoc; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null

    const focusables = (): HTMLElement[] =>
      Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      )

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) {
        e.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement as HTMLElement | null
      if (e.shiftKey) {
        if (active === first || !dialogRef.current?.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else if (active === last || !dialogRef.current?.contains(active)) {
        e.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Move focus into the dialog on open.
    focusables()[0]?.focus()

    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      previouslyFocused?.focus?.()
    }
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={doc === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className="relative flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-stone-50 shadow-xl sm:max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 rounded-full bg-stone-50/80 p-1.5 text-stone-400 hover:bg-stone-200 hover:text-stone-700"
        >
          <span className="material-symbols-outlined text-[20px]">close</span>
        </button>
        <div className="overflow-y-auto px-5 py-8 sm:px-6 sm:py-10">
          {doc === 'privacy' ? <PrivacyPage embedded /> : <TermsPage embedded />}
        </div>
      </div>
    </div>
  )
}
